import React, { useEffect, useState, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
// import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CreateIcon from '@material-ui/icons/Create';
import formatSerializedId from '../../utils/formatSerializedId';
import { i18n } from "../../translate/i18n";
import ModalImageCors from "../ModalImageCors"
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { CardHeader, Switch, Tooltip } from "@material-ui/core";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import Chip from "@material-ui/core/Chip";

import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { TagsKanbanContainer } from "../TagsKanbanContainer";


const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.inputBackground,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "50px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.inputBackground,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		justifyContent: "center",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
		borderRadius: 10,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
	memoryCard: {
		marginTop: 8,
		padding: 10,
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	memoryLine: {
		fontSize: 13,
		lineHeight: 1.4,
		wordBreak: "break-word",
	},
	memoryList: {
		margin: 0,
		paddingLeft: 18,
	},
	memoryMuted: {
		fontSize: 12,
		opacity: 0.75,
	},
	tagSection: {
		marginTop: 8,
		padding: 10,
		display: "flex",
		flexDirection: "column",
		gap: 8,
	},
	tagRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 6,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [blockingContact, setBlockingContact] = useState(contact.active);
	const [openForm, setOpenForm] = useState(false);
	const { get } = useCompanySettings();
	const [hideNum, setHideNum] = useState(false);
	const { user } = useContext(AuthContext);
	const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);
	const [excludeFromReports, setExcludeFromReports] = useState(contact.excludeFromReports);
	const [fullContact, setFullContact] = useState(contact || {});
	const displayContact = useMemo(() => {
		if (fullContact?.id) return fullContact;
		return contact || {};
	}, [fullContact, contact]);

	useEffect(() => {
		async function fetchData() {

			const lgpdHideNumber = await get({
				"column": "lgpdHideNumber"
			});

			if (lgpdHideNumber === "enabled") setHideNum(true);

		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setOpenForm(false);
		setFullContact(contact || {});
		setBlockingContact(contact?.active);
		setAcceptAudio(contact?.acceptAudioMessage);
		setExcludeFromReports(contact?.excludeFromReports);
	}, [open, contact]);

	useEffect(() => {
		let active = true;

		const loadFullContact = async () => {
			if (!open || !contact?.id) return;
			try {
				const { data } = await api.get(`/contacts/${contact.id}`);
				if (!active) return;
				setFullContact(data || {});
				setBlockingContact(data?.active);
				setAcceptAudio(data?.acceptAudioMessage);
				setExcludeFromReports(data?.excludeFromReports);
			} catch (err) {
				if (!active) return;
				toastError(err);
			}
		};

		loadFullContact();
		return () => {
			active = false;
		};
	}, [open, contact?.id]);

	const memory = useMemo(() => displayContact?.aiMemory || null, [displayContact]);
	const memoryUpdatedAt = useMemo(() => displayContact?.aiMemoryUpdatedAt || null, [displayContact]);
	const abcTags = useMemo(
		() => (displayContact?.tags || []).filter(tag => /^CLIENTE [ABC]$/i.test(String(tag?.name || "").trim())),
		[displayContact]
	);
	const nonAbcTags = useMemo(
		() => (displayContact?.tags || []).filter(tag => !/^CLIENTE [ABC]$/i.test(String(tag?.name || "").trim())),
		[displayContact]
	);

	const renderMemoryList = (items) => {
		if (!Array.isArray(items) || !items.length) return null;
		return (
			<ul className={classes.memoryList}>
				{items.map((item, index) => (
					<li key={`${item}-${index}`}>
						<Typography className={classes.memoryLine}>{item}</Typography>
					</li>
				))}
			</ul>
		);
	};

	

	const handleContactToggleAcceptAudio = async () => {
        try {
            const contact = await api.put(`/contacts/toggleAcceptAudio/${ticket.contact.id}`);
            setAcceptAudio(contact.data.acceptAudioMessage);
			setFullContact(prev => ({ ...prev, acceptAudioMessage: contact.data.acceptAudioMessage }));
        } catch (err) {
            toastError(err);
        }
    };

	const handleToggleExcludeFromReports = async () => {
		try {
			const { data } = await api.put(`/contacts/${displayContact.id}`, {
				excludeFromReports: !excludeFromReports
			});
			setExcludeFromReports(Boolean(data?.excludeFromReports));
			setFullContact(prev => ({ ...prev, excludeFromReports: Boolean(data?.excludeFromReports) }));
			toast.success(Boolean(data?.excludeFromReports) ? "Contacto excluido de informes." : "Contacto incluido nuevamente en informes.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: false });
			toast.success(i18n.t("contactDrawer.toasts.contactBlocked"));
		} catch (err) {
			toastError(err);
		}

		setBlockingContact(true);
		setFullContact(prev => ({ ...prev, active: false }));
	};

	const handleUnBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: true });
			toast.success(i18n.t("contactDrawer.toasts.contactUnblocked"));
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(false);
		setFullContact(prev => ({ ...prev, active: true }));
	};

	if (loading) return null;

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ justifySelf: "center" }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				<div>
					{!loading ? (
						<>
							<Typography
								style={{ marginBottom: 8, marginTop: 12 }}
								variant="subtitle1"
							>
								<Switch
									size="small"
									checked={acceptAudioMessage}
									onChange={() => handleContactToggleAcceptAudio()}
									name="disableBot"
									color="primary"
								/>
								{i18n.t("ticketOptionsMenu.acceptAudioMessage")}								
							</Typography>
						</>
					) : (<br />)}
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							<ModalImageCors imageUrl={displayContact?.urlPicture} />
							<CardHeader
								onClick={() => { }}
								style={{ cursor: "pointer", width: '100%' }}
								titleTypographyProps={{ noWrap: true }}
								subheaderTypographyProps={{ noWrap: true }}
								title={
									<>
										<Typography onClick={() => setOpenForm(true)}>
											{displayContact.name}
											<CreateIcon style={{ fontSize: 16, marginLeft: 5 }} />
										</Typography>
									</>
								}
								subheader={
									<>
										<Typography style={{ fontSize: 12 }}>
											{hideNum && user.profile === "user" ? formatSerializedId(displayContact.number).slice(0, -6) + "**-**" + displayContact.number.slice(-2) : formatSerializedId(displayContact.number)}
										</Typography>
										<Typography style={{ color: "primary", fontSize: 12 }}>
											<Link href={`mailto:${displayContact.email}`}>{displayContact.email}</Link>
										</Typography>
									</>
								}
							/>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => setModalOpen(!openForm)}
								style={{ fontSize: 12 }}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								onClick={() => displayContact.active
									? handleBlockContact(displayContact.id)
									: handleUnBlockContact(displayContact.id)}
								disabled={loading}
							>
								{!displayContact.active ? i18n.t("contactDrawer.buttons.unblockContact") : i18n.t("contactDrawer.buttons.blockContact")}
							</Button>
						{(displayContact.id && openForm) && <ContactForm initialContact={displayContact} onCancel={() => setOpenForm(false)} />}
						</Paper>
						<TagsKanbanContainer ticket={ticket} className={classes.contactTags} />
						<Paper square variant="outlined" className={classes.tagSection}>
							<Typography variant="subtitle1">Control gerencial</Typography>
							<Typography variant="body2" className={classes.memoryMuted}>
								Excluir este contacto de ABC, Pareto, tickets IA y calificaciones gerenciales
							</Typography>
							<Typography variant="subtitle2">
								<Switch
									size="small"
									checked={Boolean(excludeFromReports)}
									onChange={handleToggleExcludeFromReports}
									name="excludeFromReports"
									color="primary"
								/>
								Excluir de informes
							</Typography>
						</Paper>
						<Paper square variant="outlined" className={classes.tagSection}>
							<Typography variant="subtitle1">Clasificacion del cliente</Typography>
							<div className={classes.tagRow}>
								{abcTags.length > 0 ? abcTags.map(tag => (
									<Chip
										key={`abc-${tag.id}`}
										label={tag.name}
										size="small"
										style={{
											backgroundColor: tag.color || "#244565",
											color: "#fff",
											fontWeight: 700
										}}
									/>
								)) : (
									<Typography className={classes.memoryMuted}>
										Este contacto aun no tiene clasificacion gerencial aplicada.
									</Typography>
								)}
							</div>
							{nonAbcTags.length > 0 && (
								<>
									<Typography variant="body2" className={classes.memoryMuted}>
										Otras etiquetas del contacto
									</Typography>
									<div className={classes.tagRow}>
										{nonAbcTags.map(tag => (
											<Chip
												key={`tag-${tag.id}`}
												label={tag.name}
												size="small"
												style={{
													backgroundColor: tag.color || "#607d8b",
													color: "#fff"
												}}
											/>
										))}
									</div>
								</>
							)}
						</Paper>
						<Paper square variant="outlined" className={classes.memoryCard}>
							<Typography variant="subtitle1">Memoria del cliente</Typography>
							{memory ? (
								<>
									{memory.empresa ? <Typography className={classes.memoryLine}><strong>Empresa:</strong> {memory.empresa}</Typography> : null}
									{memory.setor ? <Typography className={classes.memoryLine}><strong>Sector:</strong> {memory.setor}</Typography> : null}
									{memory.assunto ? <Typography className={classes.memoryLine}><strong>Asunto:</strong> {memory.assunto}</Typography> : null}
									{memory.necessidadePrincipal ? <Typography className={classes.memoryLine}><strong>Necesidad principal:</strong> {memory.necessidadePrincipal}</Typography> : null}
									{memory.statusLead ? <Typography className={classes.memoryLine}><strong>Estado:</strong> {memory.statusLead}</Typography> : null}
									{memory.sentimento ? <Typography className={classes.memoryLine}><strong>Sentimiento:</strong> {memory.sentimento}</Typography> : null}
									{memory.resumo ? <Typography className={classes.memoryLine}><strong>Resumen:</strong> {memory.resumo}</Typography> : null}
									{memory.proximaAcao ? <Typography className={classes.memoryLine}><strong>Próxima acción:</strong> {memory.proximaAcao}</Typography> : null}
									{memory.ultimoAcordo ? <Typography className={classes.memoryLine}><strong>Último acuerdo:</strong> {memory.ultimoAcordo}</Typography> : null}
									{memory.produtosMencionados?.length ? (
										<div>
											<Typography className={classes.memoryLine}><strong>Productos mencionados:</strong></Typography>
											{renderMemoryList(memory.produtosMencionados)}
										</div>
									) : null}
									{memory.objecoes?.length ? (
										<div>
											<Typography className={classes.memoryLine}><strong>Objeciones:</strong></Typography>
											{renderMemoryList(memory.objecoes)}
										</div>
									) : null}
									{memory.dadosChave?.length ? (
										<div>
											<Typography className={classes.memoryLine}><strong>Datos clave:</strong></Typography>
											{renderMemoryList(memory.dadosChave)}
										</div>
									) : null}
									{memory.oportunidadesUpsell?.length ? (
										<div>
											<Typography className={classes.memoryLine}><strong>Oportunidades:</strong></Typography>
											{renderMemoryList(memory.oportunidadesUpsell)}
										</div>
									) : null}
									{memoryUpdatedAt ? (
										<Typography className={classes.memoryMuted}>
											Actualizada: {new Date(memoryUpdatedAt).toLocaleString("es-CO")}
										</Typography>
									) : null}
								</>
							) : (
								<Typography className={classes.memoryMuted}>
									Aún no hay memoria generada para este contacto.
								</Typography>
							)}
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<Typography variant="subtitle1" style={{ marginBottom: 10 }}>
								{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
							</Typography>
							<ContactNotes ticket={ticket} />
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							></ContactModal>
							<Typography variant="subtitle1">
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map(info => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
								>
									<InputLabel>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</Paper>
					</div>
				)}
			</Drawer>
		</>
	);
};

export default ContactDrawer;
